import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    Stack,
    Typography
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
    Event as EventIcon,
    Paid as PaidIcon,
    CalendarToday as CalendarTodayIcon,
    LocationOn as LocationOnIcon,
    Schedule as ScheduleIcon,
    Info as InfoIcon,
    Notes as NotesIcon,
    Group as GroupIcon
} from "@mui/icons-material";
import {
    cancelEventRegistration,
    getPublicEvents,
    registerForEvent
} from "../api/eventApi";
import { useNavigate } from "react-router-dom";
import { formatCurrency } from "../utils/currency";

const formatDateTime = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleString("et-EE", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit"
    });
};

const getRegistrationWindowStatus = (event) => {
    const now = new Date();
    const opensAt = event.registrationOpenAt ? new Date(event.registrationOpenAt) : null;
    const closesAt = event.registrationCloseAt ? new Date(event.registrationCloseAt) : null;

    if (opensAt && now < opensAt) {
        return {
            isOpen: false,
            severity: "info",
            message: `Registration opens ${formatDateTime(event.registrationOpenAt)}.`
        };
    }

    if (closesAt && now > closesAt) {
        return {
            isOpen: false,
            severity: "warning",
            message: `Registration closed ${formatDateTime(event.registrationCloseAt)}.`
        };
    }

    return {
        isOpen: true,
        severity: "success",
        message: "Registration is open."
    };
};

const getEventAvailability = (event) => {
    const registrations = event._count?.registrations || 0;
    const capacity = Number(event.capacity || 0);
    const isFull = capacity > 0 && registrations >= capacity;

    return {
        registrations,
        isFull,
        canJoinWaitlist: isFull && Boolean(event.allowWaitlist)
    };
};

const PublicEvents = ({ affiliateId, affiliate }) => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [actionLoadingId, setActionLoadingId] = useState(null);

    const [detailsOpen, setDetailsOpen] = useState(false);
    const [detailsEvent, setDetailsEvent] = useState(null);
    const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
    const [cancelTargetEvent, setCancelTargetEvent] = useState(null);
    const navigate = useNavigate();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
    const detailsRegistrationWindow = detailsEvent ? getRegistrationWindowStatus(detailsEvent) : null;
    const detailsAvailability = detailsEvent ? getEventAvailability(detailsEvent) : null;
    const paymentCurrency = affiliate?.paymentCurrency || "EUR";
    const formatMoney = (value, event) => formatCurrency(
        value,
        event?.paymentCurrency || event?.affiliate?.paymentCurrency || paymentCurrency
    );

    const loadEvents = async () => {
        if (!affiliateId) {
            setEvents([]);
            return;
        }
        try {
            setLoading(true);
            setError("");
            const response = await getPublicEvents(affiliateId);
            setEvents(response.events || []);
        } catch (loadError) {
            console.error("Failed to load events:", loadError);
            setError(loadError?.response?.data?.message || "Failed to load events");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void loadEvents();
    }, [affiliateId]);

    const sortedEvents = useMemo(
        () => [...events].sort((a, b) => new Date(a.eventStartAt) - new Date(b.eventStartAt)),
        [events]
    );

    const handleRegisterFree = async (event) => {
        const registrationWindow = getRegistrationWindowStatus(event);
        if (!registrationWindow.isOpen) {
            setError(registrationWindow.message);
            return;
        }

        try {
            setActionLoadingId(event.id);
            await registerForEvent(event.id);
            await loadEvents();
        } catch (registerError) {
            console.error("Free registration failed:", registerError);
            setError(registerError?.response?.data?.message || "Registration failed");
        } finally {
            setActionLoadingId(null);
        }
    };

    const handleCancelRegistration = async (event) => {
        try {
            setActionLoadingId(event.id);
            await cancelEventRegistration(event.id);
            await loadEvents();
        } catch (cancelError) {
            console.error("Cancel registration failed:", cancelError);
            setError(cancelError?.response?.data?.message || "Failed to cancel registration");
        } finally {
            setActionLoadingId(null);
        }
    };

    const requestCancelRegistration = (event) => {
        setCancelTargetEvent(event);
        setCancelConfirmOpen(true);
    };

    const openDetailsDialog = (event) => {
        setDetailsEvent(event);
        setDetailsOpen(true);
    };

    const goToEventCheckout = (event) => {
        const registrationWindow = getRegistrationWindowStatus(event);
        if (!registrationWindow.isOpen) {
            setError(registrationWindow.message);
            return;
        }

        navigate("/event-checkout/checkout", {
            state: {
                event: {
                    ...event,
                    paymentCountry: event.paymentCountry || event.affiliate?.paymentCountry || affiliate?.paymentCountry,
                    paymentCurrency: event.paymentCurrency || event.affiliate?.paymentCurrency || affiliate?.paymentCurrency,
                    isMontonioBankPaymentEnabled: event.isMontonioBankPaymentEnabled ?? event.affiliate?.isMontonioBankPaymentEnabled ?? affiliate?.isMontonioBankPaymentEnabled,
                    isMontonioCardPaymentEnabled: event.isMontonioCardPaymentEnabled ?? event.affiliate?.isMontonioCardPaymentEnabled ?? affiliate?.isMontonioCardPaymentEnabled,
                    isMontonioBlikPaymentEnabled: event.isMontonioBlikPaymentEnabled ?? event.affiliate?.isMontonioBlikPaymentEnabled ?? affiliate?.isMontonioBlikPaymentEnabled
                }
            }
        });
    };

    return (
        <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                Events
            </Typography>

            {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

            {loading ? (
                <Typography>Loading events...</Typography>
            ) : sortedEvents.length === 0 ? (
                <Card>
                    <CardContent>
                        <Typography color="text.secondary">No events available for this club right now.</Typography>
                    </CardContent>
                </Card>
            ) : (
                <Grid container spacing={2.5}>
                    {sortedEvents.map((event) => {
                        const myRegistration = event.myRegistration;
                        const isRegistered = Boolean(myRegistration);
                        const isWaitlisted = Boolean(event.myWaitlistEntry);
                        const isPaid = Number(event.price || 0) > 0;
                        const registrationWindow = getRegistrationWindowStatus(event);
                        const availability = getEventAvailability(event);
                        const registrations = availability.registrations;
                        const capacityLabel = event.capacity ? `${registrations}/${event.capacity}` : `${registrations}`;
                        const eventImage = event.imageData || event.imageUrl;

                        return (
                            <Grid item xs={12} md={6} xl={4} key={event.id}>
                                <Card
                                    variant="outlined"
                                    sx={{
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                        overflow: "hidden",
                                        borderRadius: 3,
                                        borderColor: "rgba(25, 118, 210, 0.14)",
                                        boxShadow: "0 10px 30px rgba(15, 23, 42, 0.08)",
                                        transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
                                        "&:hover": {
                                            transform: "translateY(-3px)",
                                            boxShadow: "0 16px 38px rgba(15, 23, 42, 0.13)",
                                            borderColor: "rgba(25, 118, 210, 0.35)"
                                        }
                                    }}
                                >
                                    <Box onClick={() => openDetailsDialog(event)} sx={{ cursor: "pointer", flexGrow: 1 }}>
                                        <Box
                                            sx={{
                                                height: 150,
                                                position: "relative",
                                                background: eventImage
                                                    ? "linear-gradient(135deg, rgba(25,118,210,0.15), rgba(21,101,192,0.12))"
                                                    : "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 48%, #90caf9 100%)",
                                                overflow: "hidden"
                                            }}
                                        >
                                            {eventImage ? (
                                                <Box
                                                    component="img"
                                                    src={eventImage}
                                                    alt={event.title}
                                                    sx={{
                                                        width: "100%",
                                                        height: "100%",
                                                        objectFit: "cover",
                                                        display: "block"
                                                    }}
                                                />
                                            ) : (
                                                <Stack alignItems="center" justifyContent="center" sx={{ height: "100%", color: "primary.main" }}>
                                                    <EventIcon sx={{ fontSize: 42, opacity: 0.9 }} />
                                                </Stack>
                                            )}
                                            <Stack direction="row" spacing={1} sx={{ position: "absolute", top: 12, left: 12, right: 12, flexWrap: "wrap", gap: 1 }}>
                                                <Chip
                                                    label={event.status}
                                                    color={event.status === "published" ? "success" : "default"}
                                                    size="small"
                                                    sx={{ fontWeight: 700, textTransform: "capitalize", bgcolor: event.status === "published" ? undefined : "rgba(255,255,255,0.9)" }}
                                                />
                                                {isRegistered ? <Chip color="success" label={`Registered (${myRegistration.status})`} size="small" sx={{ fontWeight: 700 }} /> : null}
                                                {isWaitlisted ? <Chip color="info" label={`Waitlist #${event.myWaitlistEntry.position}`} size="small" sx={{ fontWeight: 700 }} /> : null}
                                            </Stack>
                                        </Box>

                                        <CardContent sx={{ pb: 1.5 }}>
                                            <Stack spacing={1.5}>
                                                <Box>
                                                    <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                                                        {event.title}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{
                                                            mt: 0.75,
                                                            minHeight: 40,
                                                            display: "-webkit-box",
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: "vertical",
                                                            overflow: "hidden"
                                                        }}
                                                    >
                                                        {event.shortDescription || event.description || "No additional info"}
                                                    </Typography>
                                                </Box>

                                                <Stack spacing={1}>
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <ScheduleIcon color="primary" fontSize="small" />
                                                        <Typography variant="body2">
                                                            {formatDateTime(event.eventStartAt)}
                                                        </Typography>
                                                    </Stack>
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <LocationOnIcon color="primary" fontSize="small" />
                                                        <Typography variant="body2" color="text.secondary">
                                                            {event.location || "Location to be announced"}
                                                        </Typography>
                                                    </Stack>
                                                </Stack>

                                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                                    <Chip icon={<GroupIcon />} label={`${capacityLabel} registered`} size="small" variant="outlined" />
                                                    {isPaid ? (
                                                        <Chip icon={<PaidIcon />} color="warning" label={formatMoney(event.price, event)} size="small" />
                                                    ) : (
                                                        <Chip color="success" label="Free" size="small" variant="outlined" />
                                                    )}
                                                    {event.requiresApproval ? (
                                                        <Chip label="Approval required" size="small" variant="outlined" />
                                                    ) : null}
                                                    {availability.isFull ? (
                                                        <Chip label="Full" color="error" size="small" />
                                                    ) : null}
                                                </Stack>
                                            </Stack>
                                        </CardContent>
                                    </Box>

                                    <Box
                                        sx={{
                                            px: 2,
                                            py: 1.25,
                                            mt: "auto",
                                            borderTop: "1px solid",
                                            borderColor: "divider",
                                            bgcolor: "rgba(15, 23, 42, 0.02)"
                                        }}
                                    >
                                        <Stack spacing={1.25}>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.35 }}>
                                                    Registration opens: {formatDateTime(event.registrationOpenAt)}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.35 }}>
                                                    Registration closes: {formatDateTime(event.registrationCloseAt)}
                                                </Typography>
                                            </Box>
                                            {!registrationWindow.isOpen && !isRegistered && !isWaitlisted ? (
                                                <Alert severity={registrationWindow.severity} sx={{ py: 0 }}>
                                                    {registrationWindow.message}
                                                </Alert>
                                            ) : null}
                                            {registrationWindow.isOpen && availability.isFull && !isRegistered && !isWaitlisted ? (
                                                <Alert severity={availability.canJoinWaitlist ? "info" : "warning"} sx={{ py: 0 }}>
                                                    {availability.canJoinWaitlist ? "Event is full. You can join the waitlist." : "Event is full."}
                                                </Alert>
                                            ) : null}
                                            {isWaitlisted && (availability.isFull || !isPaid) ? (
                                                <Alert severity="info" sx={{ py: 0 }}>
                                                    You are on the waitlist.
                                                </Alert>
                                            ) : null}
                                            {!isRegistered && !isWaitlisted && !isPaid && !availability.isFull ? (
                                                <Button
                                                    fullWidth
                                                    variant="contained"
                                                    onClick={() => handleRegisterFree(event)}
                                                    disabled={actionLoadingId === event.id || !registrationWindow.isOpen}
                                                >
                                                    Register
                                                </Button>
                                            ) : null}

                                            {!isRegistered && !isWaitlisted && isPaid && !availability.isFull ? (
                                                <Button
                                                    fullWidth
                                                    variant="contained"
                                                    color="warning"
                                                    onClick={() => goToEventCheckout(event)}
                                                    disabled={actionLoadingId === event.id || !registrationWindow.isOpen}
                                                >
                                                    Buy ticket and register
                                                </Button>
                                            ) : null}

                                            {!isRegistered && isWaitlisted && isPaid && !availability.isFull ? (
                                                <Button
                                                    fullWidth
                                                    variant="contained"
                                                    color="warning"
                                                    onClick={() => goToEventCheckout(event)}
                                                    disabled={actionLoadingId === event.id || !registrationWindow.isOpen}
                                                >
                                                    Buy ticket and register
                                                </Button>
                                            ) : null}

                                            {!isRegistered && !isWaitlisted && availability.canJoinWaitlist ? (
                                                <Button
                                                    fullWidth
                                                    variant="outlined"
                                                    color="info"
                                                    onClick={() => handleRegisterFree(event)}
                                                    disabled={actionLoadingId === event.id || !registrationWindow.isOpen}
                                                >
                                                    Join waitlist
                                                </Button>
                                            ) : null}

                                            {isRegistered ? (
                                                <Button
                                                    fullWidth
                                                    variant="outlined"
                                                    color="error"
                                                    onClick={() => requestCancelRegistration(event)}
                                                    disabled={actionLoadingId === event.id}
                                                >
                                                    Cancel registration
                                                </Button>
                                            ) : null}
                                        </Stack>
                                    </Box>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            )}

            <Dialog
                open={detailsOpen}
                onClose={() => setDetailsOpen(false)}
                fullWidth
                maxWidth="md"
                fullScreen={fullScreen}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Stack spacing={0.5}>
                        <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                            {detailsEvent?.title || "Event details"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Full event info and registration status
                        </Typography>
                    </Stack>
                </DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    {detailsEvent ? (
                        <Stack spacing={2}>
                            {(detailsEvent.imageData || detailsEvent.imageUrl) ? (
                                <Box
                                    component="img"
                                    src={detailsEvent.imageData || detailsEvent.imageUrl}
                                    alt={detailsEvent.title}
                                    sx={{
                                        width: "100%",
                                        maxHeight: 420,
                                        objectFit: "contain",
                                        borderRadius: 1,
                                        backgroundColor: "grey.100",
                                        border: "1px solid",
                                        borderColor: "divider"
                                    }}
                                />
                            ) : null}
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
                                <Chip
                                    label={detailsEvent.status}
                                    size="small"
                                    sx={{ textTransform: "uppercase", fontWeight: 700 }}
                                />
                                <Chip
                                    icon={<GroupIcon sx={{ fontSize: 16 }} />}
                                    label={`${detailsEvent._count?.registrations || 0}/${detailsEvent.capacity || "∞"} registered`}
                                    size="small"
                                    variant="outlined"
                                />
                                <Chip
                                    label={Number(detailsEvent.price || 0) > 0 ? formatMoney(detailsEvent.price, detailsEvent) : "Free"}
                                    color={Number(detailsEvent.price || 0) > 0 ? "warning" : "success"}
                                    size="small"
                                    variant={Number(detailsEvent.price || 0) > 0 ? "filled" : "outlined"}
                                />
                                {detailsEvent.myRegistration?.status && (
                                    <Chip label={`My status: ${detailsEvent.myRegistration.status}`} color="success" size="small" />
                                )}
                                {detailsEvent.myWaitlistEntry?.position && (
                                    <Chip label={`Waitlist #${detailsEvent.myWaitlistEntry.position}`} color="info" size="small" />
                                )}
                                {detailsAvailability?.isFull ? (
                                    <Chip label="Full" color="error" size="small" />
                                ) : null}
                            </Stack>
                            <Card variant="outlined" sx={{ borderRadius: 2 }}>
                                <CardContent sx={{ py: 1.5 }}>
                                    <Stack spacing={1}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <LocationOnIcon color="action" fontSize="small" />
                                            <Typography variant="body2">{detailsEvent.location || "Location not specified"}</Typography>
                                        </Stack>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <CalendarTodayIcon color="action" fontSize="small" />
                                            <Typography variant="body2">
                                                {formatDateTime(detailsEvent.eventStartAt)} - {formatDateTime(detailsEvent.eventEndAt)}
                                            </Typography>
                                        </Stack>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <ScheduleIcon color="action" fontSize="small" />
                                            <Typography variant="body2">
                                                Registration: {formatDateTime(detailsEvent.registrationOpenAt)} - {formatDateTime(detailsEvent.registrationCloseAt)}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </CardContent>
                            </Card>
                            {detailsRegistrationWindow && !detailsRegistrationWindow.isOpen && !detailsEvent.myRegistration && !detailsEvent.myWaitlistEntry ? (
                                <Alert severity={detailsRegistrationWindow.severity}>
                                    {detailsRegistrationWindow.message}
                                </Alert>
                            ) : null}
                            {detailsRegistrationWindow?.isOpen && detailsAvailability?.isFull && !detailsEvent.myRegistration && !detailsEvent.myWaitlistEntry ? (
                                <Alert severity={detailsAvailability.canJoinWaitlist ? "info" : "warning"}>
                                    {detailsAvailability.canJoinWaitlist ? "Event is full. You can join the waitlist." : "Event is full."}
                                </Alert>
                            ) : null}
                            {detailsEvent.myWaitlistEntry && (detailsAvailability?.isFull || Number(detailsEvent.price || 0) <= 0) ? (
                                <Alert severity="info">
                                    You are on the waitlist.
                                </Alert>
                            ) : null}
                            <Card variant="outlined" sx={{ borderRadius: 2 }}>
                                <CardContent sx={{ py: 1.5 }}>
                                    <Stack spacing={1}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <InfoIcon color="action" fontSize="small" />
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                Short description
                                            </Typography>
                                        </Stack>
                                        <Typography variant="body2" color="text.secondary">
                                            {detailsEvent.shortDescription || "-"}
                                        </Typography>
                                    </Stack>
                                </CardContent>
                            </Card>
                            <Card variant="outlined" sx={{ borderRadius: 2 }}>
                                <CardContent sx={{ py: 1.5 }}>
                                    <Stack spacing={1}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <NotesIcon color="action" fontSize="small" />
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                Event info
                                            </Typography>
                                        </Stack>
                                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
                                            {detailsEvent.description || "-"}
                                        </Typography>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Stack>
                    ) : null}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button variant="text" onClick={() => setDetailsOpen(false)}>Close</Button>
                    {detailsEvent && !detailsEvent.myRegistration && !detailsEvent.myWaitlistEntry && Number(detailsEvent.price || 0) <= 0 && !detailsAvailability?.isFull ? (
                        <Button
                            variant="contained"
                            onClick={() => {
                                setDetailsOpen(false);
                                handleRegisterFree(detailsEvent);
                            }}
                            disabled={actionLoadingId === detailsEvent.id || !detailsRegistrationWindow?.isOpen}
                        >
                            Register
                        </Button>
                    ) : null}
                    {detailsEvent && !detailsEvent.myRegistration && !detailsEvent.myWaitlistEntry && Number(detailsEvent.price || 0) > 0 && !detailsAvailability?.isFull ? (
                        <Button
                            variant="contained"
                            color="warning"
                            onClick={() => {
                                setDetailsOpen(false);
                                goToEventCheckout(detailsEvent);
                            }}
                            disabled={actionLoadingId === detailsEvent.id || !detailsRegistrationWindow?.isOpen}
                        >
                            Buy ticket
                        </Button>
                    ) : null}
                    {detailsEvent && !detailsEvent.myRegistration && detailsEvent.myWaitlistEntry && Number(detailsEvent.price || 0) > 0 && !detailsAvailability?.isFull ? (
                        <Button
                            variant="contained"
                            color="warning"
                            onClick={() => {
                                setDetailsOpen(false);
                                goToEventCheckout(detailsEvent);
                            }}
                            disabled={actionLoadingId === detailsEvent.id || !detailsRegistrationWindow?.isOpen}
                        >
                            Buy ticket
                        </Button>
                    ) : null}
                    {detailsEvent && !detailsEvent.myRegistration && !detailsEvent.myWaitlistEntry && detailsAvailability?.canJoinWaitlist ? (
                        <Button
                            variant="outlined"
                            color="info"
                            onClick={() => {
                                setDetailsOpen(false);
                                handleRegisterFree(detailsEvent);
                            }}
                            disabled={actionLoadingId === detailsEvent.id || !detailsRegistrationWindow?.isOpen}
                        >
                            Join waitlist
                        </Button>
                    ) : null}
                    {detailsEvent?.myRegistration ? (
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={() => {
                                setDetailsOpen(false);
                                requestCancelRegistration(detailsEvent);
                            }}
                            disabled={actionLoadingId === detailsEvent.id}
                        >
                            Cancel registration
                        </Button>
                    ) : null}
                </DialogActions>
            </Dialog>

            <Dialog
                open={cancelConfirmOpen}
                onClose={() => setCancelConfirmOpen(false)}
                fullWidth
                maxWidth="sm"
                fullScreen={fullScreen}
            >
                <DialogTitle>Confirm cancellation</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to cancel registration for "{cancelTargetEvent?.title}"?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCancelConfirmOpen(false)}>
                        Keep registration
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={async () => {
                            if (!cancelTargetEvent) return;
                            setCancelConfirmOpen(false);
                            await handleCancelRegistration(cancelTargetEvent);
                        }}
                        disabled={actionLoadingId === cancelTargetEvent?.id}
                    >
                        Cancel registration
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PublicEvents;
